import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { SelectorCuentaPago } from '../components/CuentaPago';
import { ModalConfirmarCuenta, AvisoCuentaProveedor } from '../components/CuentasProveedor';
import { Badge, Spinner, Toast, css, Modal, PageHeader, EstadoBadge, IconBtn, ESTADO_COLORS, opcionesEstado, Grid, Label, MontoInput, Paginacion, ESTADO_LABEL, DestinoPagoFijo, EstadoMenu, useDeshacer, ROL_COLORS } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';

const BASE = process.env.REACT_APP_API_URL || '/api';
const ESTADOS = ['pendiente', 'aprobada', 'por_pagar', 'rechazada', 'pagada'];

// ── Exportación contable (formato "excel contabilidad") ─────────────────────
// Etiqueta del tipo de solicitud para la columna "TIPO DE COMPROBANTE"
// De dónde salió el tipo de cambio del renglón (ver utils/moneda.js en el backend)
const TC_ORIGEN_EXCEL = {
  mxn: '', estimado: 'ESTIMADO', programado: 'PROGRAMADO',
  pagado: 'REAL', sin_tipo_cambio: 'SIN T.C.',
};
const TIPO_SOL_LABEL = {
  pago: 'Gastos en General', debito: 'Débito', contribuciones: 'Contribuciones',
  activos_fijos: 'Activos Fijos', mercancias_nacionales: 'Mercancías Nacionales', nomina: 'Nómina',
};
const IMP_RATES = { IVA_16: 0.16, RET_IVA_10667: 0.106667, RET_IVA_16: 0.16, RET_ISR_125: 0.0125, RET_ISR_10: 0.10, RET_4: 0.04, RET_IEPS: 0.265, IEPS_TRAS: 0 };
// Impuestos de una base: usa el importe exacto del XML si existe; si no, tasa × base.
function calcImp(base, claves = [], importes = null) {
  const b = parseFloat(base) || 0;
  // Un código cuenta si está en `impuestos` O si trae importe exacto (cuadra con netoDe)
  const v = (c) => (claves.includes(c) || (importes && importes[c] != null))
    ? (importes && importes[c] != null ? importes[c] : b * IMP_RATES[c]) : 0;
  const iva      = v('IVA_16');
  const iepsTras = v('IEPS_TRAS'); // IEPS trasladado: suma al total (sin columna propia)
  const retIva   = v('RET_IVA_10667') + v('RET_IVA_16');
  const retIsr   = v('RET_ISR_125') + v('RET_ISR_10');
  const otras    = v('RET_4') + v('RET_IEPS');
  const total    = Math.round((b + iva + iepsTras - retIva - retIsr - otras) * 100) / 100;
  return { subtotal: b, iva, iepsTras, retIva, retIsr, total };
}
// Buscador de proveedor contra el catálogo: escribir solo filtra, el valor real se fija al seleccionar

// Fecha (YYYY-MM-DD) en horario local (evita el corrimiento de día de toISOString/UTC)
function isoLocal(d) { return d ? new Date(d).toLocaleDateString('en-CA') : ''; }
// Fecha real del pago: la capturada al subir el comprobante; si falta, del historial
function fechaPagoDe(sol) {
  if (sol.fechaPago) return sol.fechaPago;
  const e = (sol.historialEstados || []).slice().reverse().find(h => h.estado === 'pagada');
  return e?.fecha || null;
}

// `vista` permite usar esta misma pantalla como vista predefinida con estado
// fijo (ej. contador: "Solicitudes Pagadas" o "Para Provisión" con pendientes).
export default function Historial({ vista, tipo = 'pago' }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [filtros, setFiltros] = useState({ estado: vista?.estado || '', empresa: '', sucursal: '', proveedor: '', solicitante: '', desde: '', hasta: '', page: 1 });
  const [provInput, setProvInput] = useState('');
  const [solicInput, setSolicInput] = useState('');
  const [catalogos, setCatalogos] = useState({ empresas: [], sucursales: [] });
  const [rechazoModal, setRechazoModal] = useState({ open: false, id: null, motivo: '' });
  // Comprobante de pago obligatorio al marcar como pagada
  const [pagoModal, setPagoModal] = useState({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false });
  // Fecha de programación al pasar a "por pagar"
  const [progModal, setProgModal] = useState({ open: false, id: null, fecha: '' });
  // Cuenta del proveedor por confirmar: se elige antes de programar o pagar
  const [confirmaCuenta, setConfirmaCuenta] = useState(null); // { sol, estado }
  const [envioModal, setEnvioModal] = useState(null);
  // Corregir y reenviar una rechazada (solo el dueño o admin)
  const [corregirModal, setCorregirModal] = useState(null);
  const [motivosCat, setMotivosCat] = useState([]);
  const [motivoQ, setMotivoQ] = useState('');
  // Selección de cuenta contable (motivos con varias cuentas): al aprobar o al corregir
  const [cuentaModal, setCuentaModal] = useState(null);
  // Centros con presupuesto rebasado este mes → marca de alerta en filas y detalle
  const [rebasados, setRebasados] = useState({});
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const comentarioRef = useRef(null);
  // Subida del comprobante de pago desde el detalle (programación/admin/finanzas)
  const [subiendoComp, setSubiendoComp] = useState(false);
  const compInputRef = useRef(null);
  const puedeSubirComprobante = ['admin', 'programacion', 'finanzas'].includes(user?.rol);

  const { programar, aviso } = useDeshacer();
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
      if (vista?.todosTipos) params.tipo = 'all';
      else if (tipo && tipo !== 'pago') params.tipo = tipo;
      const qs = new URLSearchParams(params).toString();
      setData(await api.get(`/solicitudes?${qs}`));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filtros, tipo]);

  useEffect(() => { load(); }, [load]);

  // Descarga el historial actual (todos los registros que cumplen los filtros, no solo la
  // página visible) en el formato de "excel contabilidad" que usa el área contable.
  const [exportando, setExportando] = useState(false);
  const exportarContabilidad = async () => {
    setExportando(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
      if (vista?.todosTipos) params.tipo = 'all';
      else if (tipo && tipo !== 'pago') params.tipo = tipo;
      params.page = 1; params.limit = 100000; // todo el resultado, no paginado
      const qs = new URLSearchParams(params).toString();
      // Filtros comunes (estado/empresa/sucursal/fechas) para las otras colecciones
      const comunes = Object.fromEntries(Object.entries(filtros).filter(([k, v]) => v && k !== 'page'));
      const eqs = new URLSearchParams({ ...comunes, page: 1, limit: 100000 }).toString();
      // El contador (vista.todosTipos) une pago+separados con reembolsos/comprobaciones/finiquitos
      const [sol, reem, comp, finq] = await Promise.all([
        api.get(`/solicitudes?${qs}`),
        vista?.todosTipos ? api.get(`/reembolsos?${eqs}`).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
        vista?.todosTipos ? api.get(`/comprobaciones?${eqs}`).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
        vista?.todosTipos ? api.get(`/finiquitos?${eqs}`).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
      ]);

      const headers = [
        'ESTATUS', 'TIPO DE COMPROBANTE', 'SERIE', 'FOLIO', 'FECHA DE FACTURA', 'FECHA DE PROGRAMACION', 'FECHA DE PAGO',
        'NOMBRE EMISOR (PROVEEDOR)', 'NOMBRE RECEPTOR (COMPAÑÍA)', 'SUCURSAL', 'CENTRO DE COSTOS',
        'USO DE CFDI', 'FORMA DE PAGO',
        'METODO DE PAGO', '(MOTIVO DE PAGO)', 'CUENTA CONTABLE (MOTIVO DE PAGO)', 'DESCRIPCION CORTA',
        'SUBTOTAL', 'IVA', 'IEPS', 'RETENCION DE IVA', 'RETENCION DE ISR', 'TOTAL',
        // Moneda extranjera: los importes de arriba van en la moneda ORIGINAL de la
        // solicitud (no se tocan, para no mover las fórmulas que ya apuntan ahí) y
        // aquí al final va su equivalente en pesos, que es lo que suma el tablero.
        'MONEDA', 'TIPO DE CAMBIO', 'ORIGEN T.C.', 'TOTAL MXN',
      ];
      const rows = [];
      // `norm(s)` da el tipo de comprobante, emisor (proveedor) y motivo de cada colección
      const addItems = (items, norm) => (items || []).forEach(s => {
        const { tipoComprobante, proveedor, motivo } = norm(s);
        const estatus      = ESTADO_LABEL[s.estado] || s.estado || '';
        const fechaFactura = s.fechaSolicitud || (s.createdAt ? s.createdAt.split('T')[0] : '');
        const fechaPago    = s.estado === 'pagada' ? isoLocal(fechaPagoDe(s)) : '';
        const fechaProg    = s.fechaProgramacion ? isoLocal(s.fechaProgramacion) : '';
        // Tipo de cambio con el que se convierte a pesos cada renglón (1 en MXN).
        // Lo resuelve el backend con la misma cascada que usa el dashboard.
        const moneda = s.moneda || 'MXN';
        const esMXN  = moneda === 'MXN';
        const tc     = esMXN ? 1 : (parseFloat(s.tipoCambioAplicado) || 1);
        // fac = { folioCfdi, serie, usoCfdi } de la factura (o de la solicitud en monto simple).
        // motivoOv/cuentaOv permiten sobrescribir motivo y cuenta por renglón (débito por concepto).
        const comun = (fac, imp, motivoOv = motivo, cuentaOv = s.cuentaContable) => [
          estatus, tipoComprobante, fac.serie || '', fac.folioCfdi || '', fechaFactura, fechaProg, fechaPago,
          proveedor || '', s.empresa || '', s.sucursal || '', s.centroCostos || '', fac.usoCfdi || '', s.formaPago || '',
          fac.metodoPago || '', motivoOv || '', cuentaOv || '', s.observaciones || '',
          imp.subtotal, imp.iva, imp.iepsTras, imp.retIva, imp.retIsr, imp.total,
          moneda, esMXN ? '' : tc, esMXN ? '' : (TC_ORIGEN_EXCEL[s.origenTipoCambio] || ''),
          Math.round((imp.total || 0) * tc * 100) / 100,
        ];
        // Débito con conceptos: cada concepto es su propia línea con su cuenta contable
        if (s.tipo === 'debito' && (s.conceptosDebito || []).length) {
          s.conceptosDebito.forEach(c => {
            const m = parseFloat(c.monto) || 0, iv = parseFloat(c.iva) || 0;
            rows.push(comun({ folioCfdi: s.folioCfdi }, { subtotal: m, iva: iv, iepsTras: 0, retIva: 0, retIsr: 0, total: Math.round((m + iv) * 100) / 100 }, c.motivo, c.cuentaContable));
          });
          return;
        }
        const facturas = (s.facturas || []).filter(f => (parseFloat(f.monto) || 0) !== 0);
        if (facturas.length) {
          // Impuestos EXACTOS del XML a nivel solicitud, prorrateados por la base de cada
          // factura, para que la suma cuadre con el neto (= Dashboard / montoNeto).
          const ex = calcImp(s.monto, s.impuestos || [], s.impuestosImportes);
          const totalBase = facturas.reduce((a, f) => a + (parseFloat(f.monto) || 0), 0);
          const r2 = n => Math.round(n * 100) / 100;
          facturas.forEach(f => {
            const factor = totalBase ? (parseFloat(f.monto) || 0) / totalBase : 1 / facturas.length;
            rows.push(comun(f, {
              subtotal: parseFloat(f.monto) || 0,
              iva: r2(ex.iva * factor), iepsTras: r2(ex.iepsTras * factor),
              retIva: r2(ex.retIva * factor), retIsr: r2(ex.retIsr * factor), total: r2(ex.total * factor),
            }));
          });
        } else {
          rows.push(comun({ folioCfdi: s.folioCfdi }, calcImp(s.monto, s.impuestos || [], s.impuestosImportes)));
        }
      });

      // docs antiguos sin tipo = pago; comprobaciones usan comprobador/conceptoGasto
      addItems(sol.items,  s => ({ tipoComprobante: TIPO_SOL_LABEL[s.tipo] || 'Gastos en General', proveedor: s.proveedor, motivo: s.motivoPago }));
      addItems(reem.items, s => ({ tipoComprobante: 'Reembolso',    proveedor: s.proveedor,   motivo: s.motivoPago }));
      addItems(comp.items, s => ({ tipoComprobante: 'Comprobación', proveedor: s.comprobador, motivo: s.conceptoGasto }));
      addItems(finq.items, s => ({ tipoComprobante: 'Finiquito',    proveedor: s.proveedor,   motivo: s.motivoPago }));

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [
        { wch: 12 }, { wch: 18 }, { wch: 8 }, { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 14 },
        { wch: 30 }, { wch: 28 }, { wch: 20 }, { wch: 28 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 30 },
        { wch: 22 }, { wch: 40 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
        { wch: 9 }, { wch: 14 }, { wch: 13 }, { wch: 16 }, // moneda y conversión a pesos
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contabilidad');
      const etiqueta = (vista?.titulo || 'Historial').replace(/\s+/g, '_');
      XLSX.writeFile(wb, `Contabilidad_${etiqueta}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast_(`${rows.length} renglón${rows.length !== 1 ? 'es' : ''} exportado${rows.length !== 1 ? 's' : ''}`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setExportando(false); }
  };

  // Catálogos para los selects de empresa/sucursal (una sola vez)
  useEffect(() => {
    Promise.all([api.get('/empresas').catch(() => []), api.get('/sucursales').catch(() => [])])
      .then(([emp, suc]) => setCatalogos({ empresas: emp, sucursales: suc.map(s => s.nombre) }));
    api.get('/presupuestos/rebasados').then(d => setRebasados(d.afectados || {})).catch(() => {});
  }, []);

  // Texto de la alerta de presupuesto para un centro de costos afectado
  const alertaPresupuesto = (cc) => {
    const a = rebasados[cc];
    if (!a) return null;
    const f = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });
    return a.razon === 'centro'
      ? `Presupuesto del centro "${cc}" rebasado este mes: $${f(a.gastado)} de $${f(a.presupuesto)} autorizados`
      : `Presupuesto de la dirección "${a.direccion}" rebasado este mes: $${f(a.gastado)} de $${f(a.presupuesto)} autorizados`;
  };

  // Búsqueda de proveedor con debounce (400 ms tras dejar de escribir)
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros(f => f.proveedor === provInput ? f : { ...f, proveedor: provInput, page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [provInput]);

  // Búsqueda por solicitante con debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros(f => f.solicitante === solicInput ? f : { ...f, solicitante: solicInput, page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [solicInput]);

  // Al cambiar de empresa, limpia la sucursal si no le pertenece
  const cambiarEmpresa = (v) => setFiltros(f => {
    const emp = catalogos.empresas.find(e => e.nombre === v);
    const sucValida = !v || !emp?.sucursales?.length || emp.sucursales.includes(f.sucursal);
    return { ...f, empresa: v, sucursal: sucValida ? f.sucursal : '', page: 1 };
  });

  const empresaObj = catalogos.empresas.find(e => e.nombre === filtros.empresa);
  const sucursalesVisibles = empresaObj?.sucursales?.length
    ? catalogos.sucursales.filter(s => empresaObj.sucursales.includes(s))
    : catalogos.sucursales;

  const cambiarEstado = async (id, estado, motivo = '', extra = {}) => {
    try {
      const sol = await api.patch(`/solicitudes/${id}/estado`, { estado, motivo, ...extra });
      toast_(`Estado actualizado: ${ESTADO_LABEL[estado] || estado}`);
      if (detalle?._id === id) setDetalle(sol);
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const handleEstado = (id, estado, confirmada = false) => {
    const sol = data.items.find(i => i._id === id);
    if (!confirmada && ['por_pagar', 'pagada'].includes(estado) && sol?.cuentaPorConfirmar) {
      setConfirmaCuenta({ sol, estado });
      return;
    }
    if (estado === 'rechazada') {
      setRechazoModal({ open: true, id, motivo: '' });
    } else if (estado === 'pagada') {
      const item = data.items.find(i => i._id === id);
      const yaTiene = (item?.comprobantes || []).some(a => a.concepto === 'Comprobante de pago');
      setPagoModal({ open: true, id, empresa: item?.empresa, cuentaPago: '', files: [], yaTiene, fecha: '', subiendo: false });
    } else if (estado === 'aprobada') {
      // Sin cuenta asignada puede abrir el modal de cuenta contable: va directo, sin diferir
      if (!sol?.cuentaContable) aprobarConCuenta(id);
      else programar(`${ESTADO_LABEL[estado]} · ${sol.folio}`, () => aprobarConCuenta(id));
    } else if (estado === 'por_pagar') {
      const hoy = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
      setProgModal({ open: true, id, fecha: hoy });
    } else {
      programar(`${ESTADO_LABEL[estado] || estado} · ${sol?.folio || ''}`, () => cambiarEstado(id, estado));
    }
  };

  // Envío del comprobante de pago al proveedor. Es manual a propósito: un correo no
  // se puede deshacer y el comprobante lleva datos bancarios, así que el destinatario
  // se muestra (y se puede corregir) antes de enviar.
  const abrirEnvio = async (s) => {
    setEnvioModal({ open: true, id: s._id, folio: s.folio, proveedor: s.proveedor, cargando: true, email: '', enviando: false });
    try {
      const d = await api.get(`/solicitudes/${s._id}/destinatario`);
      setEnvioModal(m => m && m.id === s._id ? { ...m, ...d, email: d.email || '', cargando: false } : m);
    } catch (e) {
      setEnvioModal(m => m && m.id === s._id ? { ...m, cargando: false, error: e.message } : m);
    }
  };

  const confirmarEnvio = async () => {
    const { id, email } = envioModal;
    setEnvioModal(m => ({ ...m, enviando: true }));
    try {
      const r = await api.post(`/solicitudes/${id}/enviar-comprobante`, { email });
      toast_(`Comprobante enviado a ${r.email}`);
      setEnvioModal(null);
      load();
    } catch (e) {
      toast_(e.message, 'error');
      setEnvioModal(m => m && { ...m, enviando: false });
    }
  };

  const confirmarProg = async () => {
    const { id, fecha } = progModal;
    if (!fecha) { toast_('Indica la fecha de programación', 'error'); return; }
    await cambiarEstado(id, 'por_pagar', '', { fechaProgramacion: fecha });
    setProgModal({ open: false, id: null, fecha: '' });
  };

  // Al aprobar: si el motivo tiene varias cuentas contables y la solicitud aún
  // no tiene una asignada, el contador elige cuál clasifica este gasto.
  const cuentasDelMotivo = async (nombreMotivo) => {
    try {
      const lista = await api.get(`/motivos?q=${encodeURIComponent(nombreMotivo || '')}`);
      const mot = (lista || []).find(m => m.nombre.toLowerCase() === (nombreMotivo || '').trim().toLowerCase());
      return mot?.cuentasContables || [];
    } catch { return []; }
  };

  const aprobarConCuenta = async (id) => {
    const item = data.items.find(i => i._id === id);
    // Intenta aprobar directo: el backend resuelve la cuenta con la serie
    // contable del solicitante. Solo si quedó ambigua, el contador elige.
    try {
      const sol = await api.patch(`/solicitudes/${id}/estado`, { estado: 'aprobada', motivo: '' });
      toast_(`Estado actualizado: ${ESTADO_LABEL.aprobada}`);
      if (detalle?._id === id) setDetalle(sol);
      load();
    } catch (e) {
      if ((e.message || '').includes('Selecciona la cuenta contable')) {
        const cuentas = await cuentasDelMotivo(item?.motivoPago);
        setCuentaModal({ open: true, id, folio: item?.folio, motivo: item?.motivoPago, cuentas, seleccion: '', modo: 'aprobar' });
      } else toast_(e.message, 'error');
    }
  };

  const abrirEditarCuenta = async () => {
    if (!detalle) return;
    const cuentas = await cuentasDelMotivo(detalle.motivoPago);
    if (!cuentas.length) { toast_('El motivo de esta solicitud no tiene cuentas en el catálogo', 'error'); return; }
    setCuentaModal({ open: true, id: detalle._id, folio: detalle.folio, motivo: detalle.motivoPago, cuentas, seleccion: detalle.cuentaContable || '', modo: 'editar' });
  };

  const confirmarCuenta = async () => {
    const { id, seleccion, modo } = cuentaModal;
    if (!seleccion) { toast_('Selecciona la cuenta contable', 'error'); return; }
    if (modo === 'aprobar') {
      await cambiarEstado(id, 'aprobada', '', { cuentaContable: seleccion });
    } else {
      try {
        await api.patch(`/solicitudes/${id}/cuenta-contable`, { cuentaContable: seleccion });
        toast_('Cuenta contable actualizada');
        if (detalle?._id === id) setDetalle(d => ({ ...d, cuentaContable: seleccion }));
        load();
      } catch (e) { toast_(e.message, 'error'); return; }
    }
    setCuentaModal(null);
  };

  const confirmarPago = async () => {
    const { id, files, yaTiene, fecha } = pagoModal;
    if (!files.length && !yaTiene) { toast_('Adjunta el comprobante de pago (PDF o imagen)', 'error'); return; }
    if (!fecha) { toast_('Indica la fecha en que se hizo el pago', 'error'); return; }
    if (fecha > new Date().toLocaleDateString('en-CA')) { toast_('La fecha de pago no puede ser futura', 'error'); return; }
    setPagoModal(m => ({ ...m, subiendo: true }));
    try {
      if (files.length) {
        const fd = new FormData();
        files.forEach(f => fd.append('archivos', f));
        fd.append('concepto', 'Comprobante de pago');
        await api.upload(`/solicitudes/${id}/comprobantes`, fd);
      }
      await cambiarEstado(id, 'pagada', '', { fechaPago: fecha, cuentaPago: pagoModal.cuentaPago || undefined });
      setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false });
    } catch (e) {
      toast_(e.message, 'error');
      setPagoModal(m => ({ ...m, subiendo: false }));
    }
  };

  const confirmarRechazo = async () => {
    if (!rechazoModal.motivo.trim()) { toast_('El motivo de rechazo es obligatorio', 'error'); return; }
    await cambiarEstado(rechazoModal.id, 'rechazada', rechazoModal.motivo);
    setRechazoModal({ open: false, id: null, motivo: '' });
  };

  const handlePDF = async (id, folio) => {
    try {
      const blob = await api.downloadPdf(`/solicitudes/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Solicitud_${folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!['admin', 'contador'].includes(user?.rol)) return;
    const motivo = window.prompt('Motivo de borrado (obligatorio):');
    if (motivo === null) return;
    if (!motivo.trim()) { toast_('El motivo de borrado es obligatorio', 'error'); return; }
    try { await api.del(`/solicitudes/${id}`, { motivo: motivo.trim() }); toast_('Solicitud borrada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const puedeCorregir = (s) => s.estado === 'rechazada' &&
    (user?.rol === 'admin' || String(s.usuario?._id || s.usuario) === String(user?._id));

  const abrirCorregir = (s) => {
    if (!motivosCat.length) api.get('/motivos').then(setMotivosCat).catch(() => {});
    setMotivoQ('');
    setCorregirModal({
      id: s._id, folio: s.folio, motivoRechazo: s.motivoRechazo || '', nota: '', enviando: false,
      // El destino del pago se muestra pero no se edita ni se envía: sólo lectura
      destino: {
        proveedor: s.proveedor || '', beneficiario: s.beneficiario || '',
        banco: s.banco || '', clabe: s.clabe || '', cuenta: s.cuenta || '',
      },
      campos: {
        monto: String(s.monto ?? ''),
        folioCfdi: s.folioCfdi || '', observaciones: s.observaciones || '',
        motivoPago: s.motivoPago || '', direccion: s.direccion || '',
        sucursal: s.sucursal || '', centroCostos: s.centroCostos || '',
      },
    });
  };

  const confirmarReenvio = async () => {
    const { id, campos, nota } = corregirModal;
    if (!(parseFloat(campos.monto) > 0)) { toast_('El monto es obligatorio', 'error'); return; }
    setCorregirModal(m => ({ ...m, enviando: true }));
    try {
      await api.post(`/solicitudes/${id}/reenviar`, { ...campos, monto: parseFloat(campos.monto) || 0, nota });
      toast_('Solicitud corregida y reenviada: vuelve a estar pendiente de aprobación');
      setCorregirModal(null);
      load();
    } catch (e) { toast_(e.message, 'error'); setCorregirModal(m => ({ ...m, enviando: false })); }
  };

  // Ver/descargar los archivos adjuntos de la solicitud desde el detalle
  const verAdjunto = (solId, fileId) => {
    const token = localStorage.getItem('token');
    window.open(`${BASE}/solicitudes/${solId}/comprobantes/${fileId}/preview?token=${token}`, '_blank');
  };

  const descargarAdjunto = async (solId, fileId, nombre) => {
    try {
      const r = await api.downloadFile(`/solicitudes/${solId}/comprobantes/${fileId}`);
      if (!r.ok) throw new Error('Archivo no disponible');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: nombre }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Sube el comprobante de pago al expediente desde el detalle. Queda con
  // concepto "Comprobante de pago" y aparece también en Captura de Comprobantes.
  const subirComprobantePago = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !detalle) return;
    setSubiendoComp(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('archivos', f));
      fd.append('concepto', 'Comprobante de pago');
      const res = await api.upload(`/solicitudes/${detalle._id}/comprobantes`, fd);
      setDetalle(d => ({ ...d, comprobantes: res.comprobantes }));
      toast_(`Comprobante de pago subido (${res.subidos} archivo${res.subidos !== 1 ? 's' : ''})`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setSubiendoComp(false); if (compInputRef.current) compInputRef.current.value = ''; }
  };

  const handleComentario = async () => {
    if (!comentario.trim() || !detalle) return;
    setEnviandoComentario(true);
    try {
      const r = await api.post(`/solicitudes/${detalle._id}/comentarios`, { texto: comentario });
      setDetalle(d => ({ ...d, comentarios: r.comentarios }));
      setComentario('');
      if (comentarioRef.current) comentarioRef.current.focus();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setEnviandoComentario(false); }
  };

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v, page: 1 }));
  const abrirDetalle = async (s) => {
    setDetalle(s);
    setComentario('');
    // Marca los comentarios como leídos y apaga la alerta de comentarios de la fila
    if (s.comentariosNuevos > 0) {
      api.post(`/solicitudes/${s._id}/comentarios/visto`, {}).catch(() => {});
      setData(d => ({ ...d, items: d.items.map(i => i._id === s._id ? { ...i, comentariosNuevos: 0 } : i) }));
    }
    // Recargar con comentarios e historial completos
    try {
      const fresh = await api.get(`/solicitudes?${new URLSearchParams({ _id: s._id }).toString()}`);
      const found = fresh.items?.find(i => i._id === s._id);
      if (found) setDetalle(found);
    } catch {}
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      {aviso}

      <PageHeader
        icon={vista?.icono}
        title={vista?.titulo || (user?.rol === 'solicitante' ? 'Mis Solicitudes' : 'Todas las Solicitudes')}
        subtitle={`${data.total} solicitud${data.total !== 1 ? 'es' : ''} encontrada${data.total !== 1 ? 's' : ''}${vista?.subtitulo ? ` · ${vista.subtitulo}` : ''}`}
        actions={
          <>
            {vista?.exportContabilidad && (
              <button onClick={exportarContabilidad} disabled={exportando}
                style={{ ...css.btn('secundario'), fontSize: 12, opacity: exportando ? 0.6 : 1 }}
                title="Descarga el historial en el formato del área contable (Excel)">
                {exportando ? 'Exportando…' : <ConIcono nombre="descarga">Exportar Contabilidad</ConIcono>}
              </button>
            )}
            <button onClick={load} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>
              <ConIcono nombre="refrescar">Actualizar</ConIcono>
            </button>
          </>
        }
      />

      {/* Filtros */}
      <div style={{ ...css.card, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ padding: '9px 18px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em' }}><ConIcono nombre="buscar" size={13}>Filtros</ConIcono></span>
        </div>
        <div style={{ padding: '12px 18px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {vista?.estado ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, background: `${ESTADO_COLORS[vista.estado]}18`, color: ESTADO_COLORS[vista.estado], border: `1px solid ${ESTADO_COLORS[vista.estado]}40` }}>
              ● Solo {(ESTADO_LABEL[vista.estado] || vista.estado).toLowerCase()}
            </span>
          ) : (
          <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} style={{ ...css.select(false), width: 150 }}>
            <option value="">Todos los estados</option>
            {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABEL[e] || e}</option>)}
          </select>
          )}
          <select value={filtros.empresa} onChange={e => cambiarEmpresa(e.target.value)} style={{ ...css.select(false), width: 190 }}>
            <option value="">Todas las compañías</option>
            {catalogos.empresas.map(e => <option key={e._id || e.nombre} value={e.nombre}>{e.nombre}</option>)}
          </select>
          <select value={filtros.sucursal} onChange={e => setF('sucursal', e.target.value)} style={{ ...css.select(false), width: 180 }}>
            <option value="">{filtros.empresa ? `Sucursales de ${filtros.empresa}` : 'Todas las sucursales'}</option>
            {sucursalesVisibles.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={provInput} onChange={e => setProvInput(e.target.value)}
            placeholder="Buscar proveedor..." style={{ ...css.input(false), width: 190 }} />
          {user?.rol !== 'solicitante' && (
            <input value={solicInput} onChange={e => setSolicInput(e.target.value)}
              placeholder="Buscar solicitante..." style={{ ...css.input(false), width: 190 }} />
          )}
          <input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          <input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          <button onClick={() => { setProvInput(''); setSolicInput(''); setFiltros({ estado: vista?.estado || '', empresa: '', sucursal: '', proveedor: '', solicitante: '', desde: '', hasta: '', page: 1 }); }}
            style={{ ...css.btn('secundario'), fontSize: 12 }}>Limpiar</button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--panel)', border: '1.5px solid var(--hair)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, color: 'var(--steel)' }}><Icono nombre="bandeja" size={28} /></div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 5 }}>Sin solicitudes</div>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>No hay resultados para los filtros seleccionados.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                  {['Folio', 'Compañía', 'Proveedor', 'Sucursal', 'Monto', 'Estado', user?.rol !== 'solicitante' && 'Solicitante', 'Fecha', 'Acciones'].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(s => (
                  <tr key={s._id} style={{ borderBottom: '1px solid var(--hair-soft)', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--plasma-dim)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span onClick={() => abrirDetalle(s)} style={{ fontFamily: 'var(--digit-font)', fontSize: 11.5, color: 'var(--plasma)', fontWeight: 700, cursor: 'pointer', background: 'var(--plasma-dim)', padding: '2px 8px', borderRadius: 5, border: '1px solid var(--plasma-line)' }}>{s.folio}</span>
                      {s.comentariosNuevos > 0 && (
                        <span onClick={() => abrirDetalle(s)} title={`${s.comentariosNuevos} comentario(s) nuevo(s) — haz clic para leer`}
                          style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-line)', whiteSpace: 'nowrap' }}>
                          <Icono nombre="comentario" size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{s.comentariosNuevos} nuevo{s.comentariosNuevos > 1 ? 's' : ''}
                        </span>
                      )}
                      {rebasados[s.centroCostos] && (
                        <span title={alertaPresupuesto(s.centroCostos)}
                          style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-line)', whiteSpace: 'nowrap', cursor: 'help' }}>
                          <Icono nombre="alerta" size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />Presupuesto
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink)' }}>{s.empresa}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-soft)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.proveedor}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--glass)', fontSize: 12, whiteSpace: 'nowrap' }}>{s.sucursal}</td>
                    {/* Total con impuestos (montoNeto, calculado al leer); el dato guardado no cambia */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }} title={`Total con impuestos · Base: $${parseFloat(s.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}>
                      <span style={{ fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--digit-font)', fontSize: 12 }}>${parseFloat(s.montoNeto ?? s.monto ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--glass)', marginLeft: 4, fontWeight: 500 }}>{s.moneda}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {(() => { const ops = opcionesEstado(user?.rol, s); return ops ? (
                        <EstadoMenu estado={s.estado} opciones={ops} onElegir={v => handleEstado(s._id, v)} />
                      ) : (
                        <div>
                          <EstadoBadge estado={s.estado} />
                          {s.estado === 'rechazada' && s.motivoRechazo && (
                            <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.motivoRechazo}>
                              {s.motivoRechazo}
                            </div>
                          )}
                        </div>
                      ); })()}
                      {(s.cuentaPorConfirmar || s.cuentaNueva) && <div style={{ marginTop: 3 }}><AvisoCuentaProveedor s={s} compacto /></div>}
                      {s.fechaProgramacion && ['por_pagar', 'pagada'].includes(s.estado) && (
                        <div style={{ fontSize: 11.5, color: 'var(--info)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, fontFamily: 'var(--digit-font)' }} title="Fecha de programación del pago">
                          <Icono nombre="calendario" size={12} />Prog. {isoLocal(s.fechaProgramacion)}
                        </div>
                      )}
                    </td>
                    {user?.rol !== 'solicitante' && (
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--glass)' }}>{s.usuario?.nombre || '—'}</td>
                    )}
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--glass)', whiteSpace: 'nowrap' }}>
                      {new Date(s.createdAt).toLocaleDateString('es-MX')}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <IconBtn onClick={() => handlePDF(s._id, s.folio)} title="Descargar PDF" icon={<Icono nombre="descarga" size={15} />} />
                        <IconBtn onClick={() => abrirDetalle(s)} title="Ver detalle" icon={<Icono nombre="ojo" size={15} />} />
                        {puedeCorregir(s) && (
                          <IconBtn onClick={() => abrirCorregir(s)} title="Corregir y reenviar" icon={<Icono nombre="refrescar" size={15} />} />
                        )}
                        {s.estado === 'pagada' && ['admin', 'programacion', 'finanzas'].includes(user?.rol) && (
                          <IconBtn onClick={() => abrirEnvio(s)}
                            title={(s.enviosComprobante || []).some(e => e.ok)
                              ? 'Comprobante ya enviado al proveedor — reenviar'
                              : 'Enviar el comprobante de pago al proveedor'}
                            icon={<Icono nombre="correo" size={15} style={(s.enviosComprobante || []).some(e => e.ok) ? { color: 'var(--ok)' } : undefined} />} />
                        )}
                        {['admin', 'contador'].includes(user?.rol) && (
                          <IconBtn onClick={() => handleDelete(s._id)} title="Eliminar" icon={<Icono nombre="papelera" size={15} />} danger />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginacion page={filtros.page} pages={data.pages} onPage={p => setFiltros(f => ({ ...f, page: p }))} />
      </div>

      {/* Modal motivo de rechazo */}
      <Modal open={rechazoModal.open} onClose={() => setRechazoModal({ open: false, id: null, motivo: '' })} title="Motivo de Rechazo" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>Indica el motivo por el cual se rechaza esta solicitud. El solicitante podrá verlo.</div>
          <textarea
            autoFocus
            value={rechazoModal.motivo}
            onChange={e => setRechazoModal(m => ({ ...m, motivo: e.target.value }))}
            rows={3}
            placeholder="Ej: Falta documentación de soporte, monto excede presupuesto..."
            style={{ ...css.input(!rechazoModal.motivo.trim()), resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setRechazoModal({ open: false, id: null, motivo: '' })}
              style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarRechazo}
              style={{ ...css.btn('peligro'), fontSize: 13 }}>Confirmar rechazo</button>
          </div>
        </div>
      </Modal>

      {/* Modal comprobante de pago (obligatorio para marcar pagada) */}
      <Modal open={pagoModal.open} onClose={() => setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false })} title="Marcar como Pagada" width={460}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>
            Adjunta el comprobante de la transferencia o pago. Quedará guardado en los archivos con el concepto <b>"Comprobante de pago"</b>.
          </div>
          {pagoModal.yaTiene && (
            <div style={{ fontSize: 12, color: 'var(--ok)', background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', borderRadius: 8, padding: '8px 12px' }}>
              <Icono nombre="check" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Ya tiene un comprobante de pago adjunto; puedes confirmar sin subir otro.
            </div>
          )}
          <input type="file" multiple accept=".pdf,.xml,.jpg,.jpeg,.png,.webp"
            onChange={e => setPagoModal(m => ({ ...m, files: Array.from(e.target.files || []) }))} style={{ fontSize: 12.5 }} />
          {pagoModal.files.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}><Icono nombre="clip" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />{pagoModal.files.map(f => f.name).join(' · ')}</div>
          )}
          <SelectorCuentaPago empresa={pagoModal.empresa} value={pagoModal.cuentaPago} onChange={v => setPagoModal(m => ({ ...m, cuentaPago: v }))} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de pago</span>
            <input type="date" value={pagoModal.fecha} max={new Date().toLocaleDateString('en-CA')} required
              onChange={e => setPagoModal(m => ({ ...m, fecha: e.target.value }))}
              style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
            <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>La fecha en que salió el pago del banco (no la de hoy). Aparece en el reporte y mide cuánto tardamos en subir el comprobante.</span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false })} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarPago} disabled={pagoModal.subiendo} style={{ ...css.btn('primario'), fontSize: 13, opacity: pagoModal.subiendo ? 0.6 : 1 }}>
              {pagoModal.subiendo ? 'Subiendo…' : 'Confirmar pago'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal fecha de programación (al pasar a "por pagar") */}
      {/* Envío del comprobante de pago al proveedor */}
      <Modal open={!!envioModal} onClose={() => setEnvioModal(null)} title={`Enviar comprobante ${envioModal?.folio || ''}`} width={480}>
        {envioModal?.cargando ? <Spinner /> : envioModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {envioModal.configurado === false && (
              <div style={{ padding: '10px 12px', background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', borderRadius: 8, fontSize: 12.5, color: 'var(--danger)' }}>
                El envío de correo no está configurado en el servidor. Define <b>SMTP_USER</b> y <b>SMTP_PASS</b> antes de usar esta opción.
              </div>
            )}
            {!envioModal.encontrado && (
              <div style={{ padding: '10px 12px', background: 'var(--warn-bg)', border: '1px solid var(--warn-line)', borderRadius: 8, fontSize: 12.5, color: 'var(--warn)' }}>
                <b>{envioModal.proveedor}</b> no está en el catálogo de proveedores, así que no se pudo tomar su correo. Captúralo abajo.
              </div>
            )}
            {envioModal.encontrado && !envioModal.email && (
              <div style={{ padding: '10px 12px', background: 'var(--warn-bg)', border: '1px solid var(--warn-line)', borderRadius: 8, fontSize: 12.5, color: 'var(--warn)' }}>
                <b>{envioModal.proveedor}</b> no tiene correo capturado en el catálogo. Escríbelo abajo, y conviene guardarlo en el catálogo para la próxima.
              </div>
            )}

            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Enviar a</div>
              <input value={envioModal.email} onChange={e => setEnvioModal(m => ({ ...m, email: e.target.value }))}
                placeholder="correo@proveedor.com" style={{ ...css.input(false), width: '100%', fontFamily: 'var(--digit-font)' }} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 5 }}>
                Revisa la dirección antes de enviar: el comprobante lleva datos del pago y un correo no se puede recuperar.
              </div>
            </div>

            <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', background: 'var(--hair-soft)', borderRadius: 8, padding: '9px 12px' }}>
              <Icono nombre="clip" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Se adjunta: <b>{envioModal.archivo || '— sin comprobante —'}</b>
            </div>

            {(envioModal.envios || []).length > 0 && (
              <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Envíos anteriores</div>
                {envioModal.envios.slice().reverse().map((e, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: e.ok ? 'var(--ok)' : 'var(--danger)', marginBottom: 3 }}>
                    <Icono nombre={e.ok ? 'check' : 'cerrar'} size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />{e.email} · {new Date(e.fecha).toLocaleString('es-MX')} · {e.nombreUsuario}
                    {!e.ok && e.error && <div style={{ color: 'var(--danger)', fontSize: 11.5, paddingLeft: 14 }}>{e.error}</div>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEnvioModal(null)} style={css.btn('secundario')}>Cancelar</button>
              <button onClick={confirmarEnvio}
                disabled={envioModal.enviando || !envioModal.email || !envioModal.archivo || envioModal.configurado === false}
                style={{ ...css.btn('primario'), fontSize: 13, opacity: (envioModal.enviando || !envioModal.email || !envioModal.archivo || envioModal.configurado === false) ? 0.5 : 1 }}>
                {envioModal.enviando ? 'Enviando…' : <ConIcono nombre="correo">Enviar comprobante</ConIcono>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ModalConfirmarCuenta solicitud={confirmaCuenta?.sol} onClose={() => setConfirmaCuenta(null)}
        onConfirmada={(cambios) => {
          const { sol, estado } = confirmaCuenta;
          setData(d => ({ ...d, items: d.items.map(i => (i._id === sol._id ? { ...i, ...cambios } : i)) }));
          if (detalle?._id === sol._id) setDetalle(d => ({ ...d, ...cambios }));
          setConfirmaCuenta(null);
          toast_('Cuenta confirmada');
          if (estado) handleEstado(sol._id, estado, true);
        }} />

      <Modal open={progModal.open} onClose={() => setProgModal({ open: false, id: null, fecha: '' })} title="Programar pago" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>
            Indica la <b>fecha de programación</b> del pago. Aparecerá en el reporte, en Para Provisión y en el historial.
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de programación</span>
            <input type="date" value={progModal.fecha}
              onChange={e => setProgModal(m => ({ ...m, fecha: e.target.value }))}
              style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setProgModal({ open: false, id: null, fecha: '' })} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarProg} style={{ ...css.btn('primario'), fontSize: 13 }}><ConIcono nombre="calendario">Confirmar programación</ConIcono></button>
          </div>
        </div>
      </Modal>

      {/* Modal cuenta contable (motivo con varias cuentas) */}
      <Modal open={!!cuentaModal?.open} onClose={() => setCuentaModal(null)} title="Cuenta contable del gasto" width={480}>
        {cuentaModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>
              El motivo <b style={{ color: 'var(--ink)' }}>{cuentaModal.motivo}</b> existe en varias cuentas del plan contable.
              Elige la que clasifica este gasto ({cuentaModal.folio}){cuentaModal.modo === 'aprobar' ? ' para aprobarlo' : ''}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cuentaModal.cuentas.map(c => (
                <label key={c} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${cuentaModal.seleccion === c ? 'var(--plasma)' : 'var(--hair)'}`,
                  background: cuentaModal.seleccion === c ? 'var(--plasma-dim)' : 'var(--panel)',
                }}>
                  <input type="radio" name="cuentaContable" checked={cuentaModal.seleccion === c}
                    onChange={() => setCuentaModal(m => ({ ...m, seleccion: c }))} style={{ accentColor: 'var(--plasma)' }} />
                  <span style={{ fontFamily: 'var(--digit-font)', fontSize: 13, color: 'var(--ink)' }}>{c}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--glass)' }}>
                    {c.startsWith('6000') ? 'Operación / Distribución' : c.startsWith('6100') ? 'Venta' : c.startsWith('6200') ? 'Administración' : c.startsWith('6300') ? 'Depreciación' : c.startsWith('6400') ? 'Amortización' : ''}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCuentaModal(null)} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarCuenta} style={{ ...css.btn('primario'), fontSize: 13 }}>
                {cuentaModal.modo === 'aprobar' ? 'Aprobar con esta cuenta' : 'Guardar cuenta'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal corregir y reenviar */}
      <Modal open={!!corregirModal} onClose={() => setCorregirModal(null)} title={`Corregir y reenviar ${corregirModal?.folio || ''}`} width={560}>
        {corregirModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            {corregirModal.motivoRechazo && (
              <div style={{ padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid var(--danger-line)', color: 'var(--danger)', fontSize: 12.5 }}>
                <b>Motivo del rechazo:</b> {corregirModal.motivoRechazo}
              </div>
            )}
            <DestinoPagoFijo campos={[
              ['Proveedor', corregirModal.destino.proveedor], ['Beneficiario', corregirModal.destino.beneficiario],
              ['Banco', corregirModal.destino.banco], ['CLABE', corregirModal.destino.clabe],
              ['Cuenta', corregirModal.destino.cuenta],
            ]} />
            <Grid cols={2} gap={10}>
              <div><Label required>Monto</Label><MontoInput value={corregirModal.campos.monto} onChange={v => setCorregirModal(m => ({ ...m, campos: { ...m.campos, monto: v } }))} /></div>
              <div><Label>Folio CFDI</Label><input value={corregirModal.campos.folioCfdi} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, folioCfdi: e.target.value } }))} style={css.input(false)} /></div>
              <div><Label>Dirección</Label><input value={corregirModal.campos.direccion} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, direccion: e.target.value } }))} style={css.input(false)} /></div>
              <div><Label>Sucursal</Label><input value={corregirModal.campos.sucursal} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, sucursal: e.target.value } }))} style={css.input(false)} /></div>
              <div><Label>Centro de costos</Label><input value={corregirModal.campos.centroCostos} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, centroCostos: e.target.value } }))} style={css.input(false)} /></div>
            </Grid>
            <div>
              <Label>Motivo de pago</Label>
              {corregirModal.campos.motivoPago ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid var(--ok-line)' }}>{corregirModal.campos.motivoPago}</span>
                  <button onClick={() => { setCorregirModal(m => ({ ...m, campos: { ...m.campos, motivoPago: '' } })); setMotivoQ(''); }} title="Cambiar motivo"
                    style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--glass)', cursor: 'pointer', fontSize: 13 }}>×</button>
                </div>
              ) : (
                <div>
                  <input value={motivoQ} onChange={e => setMotivoQ(e.target.value)} placeholder="Buscar motivo de pago..." style={css.input(false)} />
                  <div style={{ marginTop: 4, border: '1px solid var(--hair)', borderRadius: 10, maxHeight: 140, overflowY: 'auto', background: 'var(--panel)' }}>
                    {(() => {
                      const t = motivoQ.trim().toLowerCase();
                      const lista = (t ? motivosCat.filter(mo => mo.nombre.toLowerCase().includes(t)) : motivosCat).slice(0, 30);
                      if (!motivosCat.length) return <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--glass)' }}>Cargando motivos…</div>;
                      if (!lista.length) return <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--glass)' }}>Sin coincidencias.</div>;
                      return lista.map(mo => (
                        <div key={mo._id} onClick={() => setCorregirModal(m => ({ ...m, campos: { ...m.campos, motivoPago: mo.nombre } }))}
                          style={{ padding: '6px 12px', fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer', borderBottom: '1px solid var(--hair-soft)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--plasma-dim)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {mo.nombre}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
            <div><Label>Concepto del pago</Label>
              <textarea value={corregirModal.campos.observaciones} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, observaciones: e.target.value } }))} rows={2} style={{ ...css.input(false), resize: 'vertical' }} /></div>
            <div><Label>Nota del reenvío (qué se corrigió)</Label>
              <input value={corregirModal.nota} onChange={e => setCorregirModal(m => ({ ...m, nota: e.target.value }))} placeholder="Ej: Se corrigió el monto y el concepto" style={css.input(false)} /></div>
            <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>Si falta un documento, adjúntalo desde el detalle antes de reenviar. Al reenviar vuelve a quedar pendiente de aprobación.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCorregirModal(null)} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarReenvio} disabled={corregirModal.enviando} style={{ ...css.btn('primario'), fontSize: 13, opacity: corregirModal.enviando ? 0.6 : 1 }}>
                {corregirModal.enviando ? 'Reenviando…' : 'Reenviar solicitud'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal detalle */}
      {/* Base: los modales de acción (rechazo, pago, cuenta contable, corregir) se apilan encima */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={`Solicitud ${detalle?.folio}`} width={620} zIndex={900}>
        {detalle && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13 }}>
            {/* Alerta de presupuesto del centro de costos */}
            {rebasados[detalle.centroCostos] && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
                <ConIcono nombre="alerta" size={14}>{alertaPresupuesto(detalle.centroCostos)}</ConIcono>
              </div>
            )}
            {/* Datos */}
            <div style={{ display: 'grid', gap: 0 }}>
              {[
                ['Compañía', detalle.empresa], ['Proveedor', detalle.proveedor],
                ['Banco', detalle.cuentaPorConfirmar ? <AvisoCuentaProveedor s={detalle} /> : detalle.banco],
                ['CLABE', detalle.cuentaPorConfirmar ? (['admin', 'finanzas', 'programacion'].includes(user?.rol) && !['pagada', 'rechazada'].includes(detalle.estado)
                  ? <button onClick={() => setConfirmaCuenta({ sol: detalle, estado: null })} style={{ ...css.btn('secundario'), fontSize: 12, padding: '4px 10px' }}>Elegir cuenta</button>
                  : 'Por confirmar') : (
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>{detalle.clabe}<AvisoCuentaProveedor s={detalle} /></span>
                )],
                ['Cuenta confirmada por', detalle.cuentaConfirmada?.por ? `${detalle.cuentaConfirmada.por} · ${new Date(detalle.cuentaConfirmada.fecha).toLocaleDateString('es-MX')}` : null],
                ['Sucursal', detalle.sucursal], ['Centro de Costos', detalle.centroCostos],
                ['Monto (base)', `$${parseFloat(detalle.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${detalle.moneda}`],
                ['Total c/ impuestos', detalle.montoNeto != null && detalle.montoNeto !== detalle.monto
                  ? `$${parseFloat(detalle.montoNeto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${detalle.moneda}` : null],
                ['Forma de Pago', detalle.formaPago], ['CFDI', detalle.folioCfdi],
                ['Motivo', detalle.motivoPago], ['Concepto del Pago', detalle.observaciones],
                ['Estado', ESTADO_LABEL[detalle.estado] || detalle.estado], ['Pagada desde', detalle.cuentaPago ? [detalle.cuentaPago.banco, detalle.cuentaPago.terminacion && `••${detalle.cuentaPago.terminacion}`].filter(Boolean).join(' ') : null], ['Solicitante', detalle.usuario?.nombre],
                // Clasificación contable: visible para contador/admin (editable) o si ya está fijada
                ['Cuenta contable', (detalle.cuentaContable || ['contador', 'admin'].includes(user?.rol)) ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--digit-font)', color: detalle.cuentaContable ? 'var(--ok)' : 'var(--warn)' }}>
                      {detalle.cuentaContable || 'sin clasificar'}
                    </span>
                    {['contador', 'admin'].includes(user?.rol) && (
                      <button onClick={abrirEditarCuenta} title="Cambiar cuenta contable" aria-label="Cambiar cuenta contable"
                        style={{ padding: '1px 8px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)', cursor: 'pointer', fontSize: 11.5, display: 'inline-flex', alignItems: 'center' }}><Icono nombre="editar" size={13} /></button>
                    )}
                  </span>
                ) : null],
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--glass)', width: 130, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: 1 }}>{l}</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              {detalle.motivoRechazo && (
                <div style={{ display: 'flex', gap: 10, padding: '8px', background: 'var(--danger-bg)', borderRadius: 6, border: '1px solid var(--danger-line)', marginTop: 4 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--danger)', width: 130, flexShrink: 0, textTransform: 'uppercase', fontWeight: 700 }}>Motivo rechazo</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{detalle.motivoRechazo}</span>
                </div>
              )}
            </div>

            {/* Archivos adjuntos (XML/PDF de facturas, comprobantes de pago) */}
            {(detalle.comprobantes?.length > 0 || puedeSubirComprobante) && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Archivos adjuntos{detalle.comprobantes?.length ? ` (${detalle.comprobantes.length})` : ''}
                  </div>
                  {puedeSubirComprobante && (
                    <>
                      <input ref={compInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple style={{ display: 'none' }}
                        onChange={e => subirComprobantePago(e.target.files)} />
                      <button onClick={() => compInputRef.current?.click()} disabled={subiendoComp}
                        title="Sube el comprobante de pago; quedará en el expediente y en Captura de Comprobantes"
                        style={{ ...css.btn('secundario'), fontSize: 12, padding: '6px 12px', opacity: subiendoComp ? 0.6 : 1 }}>
                        {subiendoComp ? 'Subiendo…' : <ConIcono nombre="subir">Subir comprobante de pago</ConIcono>}
                      </button>
                    </>
                  )}
                </div>
                {detalle.comprobantes?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {detalle.comprobantes.map(a => (
                    <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderRadius: 8, background: 'var(--panel)', border: '1px solid var(--hair)' }}>
                      <Icono nombre={['xml', 'pdf'].includes(a.tipo) ? 'documento' : 'imagen'} size={15} style={{ flexShrink: 0, color: 'var(--steel)' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nombreOriginal}</div>
                        {a.concepto && <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{a.concepto}</div>}
                      </div>
                      {['pdf', 'ticket'].includes(a.tipo) && (
                        <button onClick={() => verAdjunto(detalle._id, a._id)} title="Visualizar en el navegador"
                          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}><ConIcono nombre="ojo" size={13}>Ver</ConIcono></button>
                      )}
                      <button onClick={() => descargarAdjunto(detalle._id, a._id, a.nombreOriginal)} title="Descargar" aria-label="Descargar"
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}><Icono nombre="descarga" size={13} /></button>
                    </div>
                  ))}
                </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--glass)' }}>Aún no hay archivos adjuntos. Sube aquí el comprobante de pago.</div>
                )}
              </div>
            )}

            {/* Historial de estados */}
            {detalle.historialEstados?.length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Historial de estados</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {detalle.historialEstados.map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, background: `${ESTADO_COLORS[h.estado]}18`, color: ESTADO_COLORS[h.estado], border: `1px solid ${ESTADO_COLORS[h.estado]}40`, whiteSpace: 'nowrap' }}>{ESTADO_LABEL[h.estado] || h.estado}</span>
                      <span style={{ color: 'var(--glass)' }}>{h.nombreUsuario}</span>
                      {h.motivo && <span style={{ color: 'var(--glass)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{h.motivo}"</span>}
                      <span style={{ color: 'var(--glass)', marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: 11.5 }}>{new Date(h.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comentarios */}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Comentarios {detalle.comentarios?.length > 0 && `(${detalle.comentarios.length})`}
              </div>
              {detalle.comentarios?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 180, overflowY: 'auto' }}>
                  {detalle.comentarios.map((c, i) => (
                    <div key={i} style={{ background: 'var(--hair-soft)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--hair)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{c.nombre}</span>
                          <span style={{ fontSize: 11.5, padding: '1px 6px', borderRadius: 4, background: ROL_COLORS[c.rol] ? `${ROL_COLORS[c.rol]}18` : 'var(--panel-hi)', color: ROL_COLORS[c.rol] || 'var(--glass)', fontWeight: 600, border: `1px solid ${ROL_COLORS[c.rol] ? `${ROL_COLORS[c.rol]}25` : 'var(--mesh-soft)'}` }}>{c.rol}</span>
                        </div>
                        <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>{new Date(c.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{c.texto}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--glass)', marginBottom: 8 }}>Sin comentarios aún.</div>
              )}
              {user?.rol !== 'director' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={comentarioRef} value={comentario} onChange={e => setComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComentario(); } }}
                  placeholder="Escribe un comentario... (Enter para enviar)"
                  style={{ ...css.input(false), flex: 1, fontSize: 12.5 }} />
                <button onClick={handleComentario} disabled={!comentario.trim() || enviandoComentario}
                  style={{ ...css.btn('primario'), fontSize: 12, padding: '0 16px', flexShrink: 0 }}>
                  {enviandoComentario ? '...' : 'Enviar'}
                </button>
              </div>
              )}
            </div>

            <button onClick={() => handlePDF(detalle._id, detalle.folio)} style={{ ...css.btn('secundario') }}><ConIcono nombre="descarga">Descargar PDF</ConIcono></button>
          </div>
        )}
      </Modal>
    </div>
  );
}
